import axios from 'axios';
import { PURCHASE_PROGRAM_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

const client = axios.create({
  baseURL: PURCHASE_PROGRAM_API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders).length) {
    config.headers = { ...authHeaders, ...config.headers };
  }
  return config;
});
attachAuthInterceptor(client);

const extractError = (error) => {
  if (error?.response?.data?.detail) {
    return Array.isArray(error.response.data.detail)
      ? error.response.data.detail.join(', ')
      : error.response.data.detail;
  }
  return error.message || 'Request failed';
};

const wrap = async (fn) => {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

// ─── Programs ──────────────────────────────────────────────────────────────

export const listAdminPrograms = (includeInactive = true) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/programs', { params: { include_inactive: includeInactive } }));

export const getAdminProgram = (programId) =>
  wrap(() => client.get(`/api/v1/purchase-program/admin/programs/${programId}`));

export const createAdminProgram = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/admin/programs', payload));

export const updateAdminProgram = (programId, payload) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/programs/${programId}`, payload));

export const deleteAdminProgram = (programId) =>
  wrap(() => client.delete(`/api/v1/purchase-program/admin/programs/${programId}`));

export const downloadProgramsExport = async () => {
  const response = await client.get(
    '/api/v1/purchase-program/admin/programs/export',
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  a.href = url;
  a.download = `purchase-programs-${today}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const replaceProgramListAssignments = (programId, listIds) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/programs/${programId}/list-assignments`, { list_ids: listIds }));

export const getProgramUsageStats = (programId) =>
  wrap(() => client.get(`/api/v1/purchase-program/admin/programs/${programId}/usage-stats`));

export const getScopingOptions = () =>
  wrap(() => client.get('/api/v1/purchase-program/admin/scoping-options'));

export const getCommerceContext = (siteId = null, customerId = null) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/commerce-context', {
    params: {
      ...(siteId ? { site_id: siteId } : {}),
      ...(customerId ? { customer_id: customerId } : {}),
    },
  }));

export const getCommerceUsers = (customerId) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/commerce-users', {
    params: { customer_id: customerId },
  }));

// ─── User groups ───────────────────────────────────────────────────────────

export const listUserGroups = () =>
  wrap(() => client.get('/api/v1/purchase-program/admin/user-groups'));

export const createUserGroup = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/admin/user-groups', payload));

export const updateUserGroupMembers = (groupId, members) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/user-groups/${groupId}/members`, { members }));

export const deleteUserGroup = (groupId) =>
  wrap(() => client.delete(`/api/v1/purchase-program/admin/user-groups/${groupId}`));

export const getGroupMembers = (groupId) =>
  wrap(() => client.get(`/api/v1/purchase-program/admin/user-groups/${groupId}/members`));

export const uploadUserList = (groupId, formData) =>
  wrap(() => client.post(`/api/v1/purchase-program/admin/user-groups/${groupId}/upload-users`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }));

// ─── Product tags ──────────────────────────────────────────────────────────

export const listProductTags = () =>
  wrap(() => client.get('/api/v1/purchase-program/admin/product-tags'));

export const setProductTags = (productCode, tags) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/product-tags/${encodeURIComponent(productCode)}`, { tags }));

// ─── Product lists ─────────────────────────────────────────────────────────

export const listProductLists = () =>
  wrap(() => client.get('/api/v1/purchase-program/admin/product-lists'));

export const createProductList = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/admin/product-lists', payload));

export const getProductList = (listId) =>
  wrap(() => client.get(`/api/v1/purchase-program/admin/product-lists/${listId}`));

export const updateProductList = (listId, payload) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/product-lists/${listId}`, payload));

export const deleteProductList = (listId) =>
  wrap(() => client.delete(`/api/v1/purchase-program/admin/product-lists/${listId}`));

export const setProductListItems = (listId, productCodes) =>
  wrap(() => client.put(`/api/v1/purchase-program/admin/product-lists/${listId}/items`, { product_codes: productCodes }));

export const previewImportFile = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return wrap(() => client.post('/api/v1/purchase-program/admin/product-lists/import-preview', fd));
};

export const importProductListItems = (listId, file, column, field) => {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('column', column);
  fd.append('field', field);
  return wrap(() => client.post(`/api/v1/purchase-program/admin/product-lists/${listId}/import`, fd));
};

// ─── Product search ────────────────────────────────────────────────────────

export const searchProducts = ({ q = '', category = '', manufacturer = '', programId, page = 1, pageSize = 48 } = {}) =>
  wrap(() => client.get('/api/v1/purchase-program/products/search', {
    params: {
      q: q || undefined,
      category: category || undefined,
      manufacturer: manufacturer || undefined,
      program_id: programId ?? undefined,
      page,
      page_size: pageSize,
    },
  }));

// ─── Bulk purchase ─────────────────────────────────────────────────────────

export const createBulkPurchase = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/admin/bulk-purchase', payload));

export const listBulkPurchases = (limit = 50) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/bulk-purchases', { params: { limit } }));

// ─── Purchase history ──────────────────────────────────────────────────────

export const listAdminPurchases = ({ programId, userId, dateFrom, dateTo, isAdminOnly, limit = 100, offset = 0 } = {}) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/purchases', {
    params: {
      program_id: programId ?? undefined,
      user_id: userId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      is_admin_only: isAdminOnly ?? undefined,
      limit,
      offset,
    },
  }));

// ─── Client ────────────────────────────────────────────────────────────────

export const getClientAvailable = ({ userId, siteId, customerId, shipToId, search }) =>
  wrap(() => client.get('/api/v1/purchase-program/client/available', {
    params: {
      user_id: userId,
      site_id: siteId || undefined,
      customer_id: customerId || undefined,
      ship_to_id: shipToId || undefined,
      search: search || undefined,
    },
  }));

export const validateClientPurchase = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/client/validate', payload));

export const createClientPurchase = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/client/purchase', payload));

export const createClientOrder = (payload) =>
  wrap(() => client.post('/api/v1/purchase-program/client/order', payload));

// ─── Activity log ──────────────────────────────────────────────────────────────

export const getActivityLog = ({
  programId, userId, proxyUserId, customerId, shipToId,
  sessionId, actionType, dateFrom, dateTo, limit = 100, offset = 0,
} = {}) =>
  wrap(() => client.get('/api/v1/purchase-program/admin/activity', {
    params: {
      program_id: programId || undefined,
      user_id: userId || undefined,
      proxy_user_id: proxyUserId || undefined,
      customer_id: customerId || undefined,
      ship_to_id: shipToId || undefined,
      session_id: sessionId || undefined,
      action_type: actionType || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      limit,
      offset,
    },
  }));

// ─── AI / Wizard program creation ─────────────────────────────────────────────

export const aiDiagnostics = () =>
  wrap(() => client.get('/api/v1/purchase-program/ai/diagnostics'));

export const aiStartSession = (userId = '') =>
  wrap(() => client.post('/api/v1/purchase-program/ai/session/start', { user_id: userId }));

export const aiChatMessage = ({ sessionId, message, userId = '' }) =>
  wrap(() => client.post('/api/v1/purchase-program/ai/session/chat', {
    session_id: sessionId ?? undefined,
    message,
    user_id: userId,
  }));

export const listAiSessions = ({ userId, status = 'in_progress' } = {}) =>
  wrap(() => client.get('/api/v1/purchase-program/ai/sessions', {
    params: { user_id: userId || undefined, status },
  }));

export const getAiSession = (sessionId) =>
  wrap(() => client.get(`/api/v1/purchase-program/ai/sessions/${sessionId}`));

export const wizardStartSession = (userId = '') =>
  wrap(() => client.post('/api/v1/purchase-program/wizard/session/start', { user_id: userId }));

export const wizardSaveStep = (sessionId, { step, answers }) =>
  wrap(() => client.put(`/api/v1/purchase-program/wizard/sessions/${sessionId}/step`, { step, answers }));
